import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { ApiError } from '@/util/errors/api-error'
import { MedSummaryEntriesActions } from '@/medical-billing-summaries/entries/store/entries.slice'
import { MedSummaryReferencesActions } from '@/medical-billing-summaries/references/store/slice'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { kMedSummaryAssetPollIntervalsMs } from '../constants'
import { getMedSummaryAssetByMatter } from '../fetch/get-med-summary-asset-by-matter'
import { getMedSummaryAsset } from '../fetch/get-med-summary-asset'
import { getMedSummaryAssetEntries } from '../fetch/get-med-summary-asset-entries'
import { getMedSummaryAssetFiles } from '../fetch/get-med-summary-asset-files'
import { getMedSummaryAssetUpdates } from '../fetch/get-med-summary-asset-updates'
import { getUpdateStatus } from '../fetch/get-update-status'
import { postInitializeSummary } from '../fetch/post-initialize-summary'
import { postApplyUpdate } from '../fetch/post-apply-update'
import { postRetryFailed } from '../fetch/post-retry-failed'
import { postCompleteExcludingFailed } from '../fetch/post-complete-excluding-failed'
import { kActiveMedSummaryUpdateStatuses, MedSummaryUpdateStatus } from './schemas'
import { MedSummaryAssetActions } from './slice'

export const medSummaryAssetListenerMiddleware = createListenerMiddleware()
const startListener = medSummaryAssetListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

const kConflictMessage = 'The billing summary is being updated. Try again when the current update finishes.'
const kGenericMutationMessage = 'Something went wrong. Please try again.'

// Give up polling after this many consecutive status-fetch failures (transient errors retry).
const kMedSummaryAssetMaxPollFailures = 5

// Assets with an in-flight poll loop. De-duping per asset (instead of a global
// listener unsubscribe) lets different matters/assets poll concurrently — a global
// take-leading guard dropped a second matter's poll when you switched to it and never
// resumed it. Exported so tests can reset it between cases (module-level state).
export const pollingAssetIds = new Set<string>()

/**
 * Listener: Fetch Asset For Matter
 * Loads the matter's living billing summary overview (or its confirmed absence), then resumes
 * polling of any in-flight update run and refreshes entries/files when stale.
 */
startListener({
  actionCreator: MedSummaryAssetActions.fetchAssetForMatter,
  effect: async (action, listenerApi) => {
    const { matterId } = action.payload
    const state = listenerApi.getState().medSummaryAssetState

    if (state.assetLoadingByMatterId[matterId]) return

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setAssetLoading({ matterId, isLoading: true }))
      listenerApi.dispatch(MedSummaryAssetActions.setAssetError({ matterId, hasError: false }))

      const response = await getMedSummaryAssetByMatter(matterId)
      listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId, asset: response.asset }))

      const asset = response.asset
      if (!asset) return

      // Resume polling any in-flight run (FAILED_FILES gets a one-shot status fetch inside)
      if (asset.active_update) {
        listenerApi.dispatch(MedSummaryAssetActions.pollActiveUpdate({ assetId: asset.id, updateId: asset.active_update.id }))
      }

      // Refresh entries/files when the live summary version is not the one loaded
      const loadedVersion = listenerApi.getState().medSummaryAssetState.loadedEntriesVersionByAssetId[asset.id]
      if (loadedVersion !== asset.summary_version) {
        listenerApi.dispatch(MedSummaryAssetActions.refreshAssetData({ assetId: asset.id }))
      }
    } catch {
      listenerApi.dispatch(MedSummaryAssetActions.setAssetError({ matterId, hasError: true }))
    } finally {
      listenerApi.dispatch(MedSummaryAssetActions.setAssetLoading({ matterId, isLoading: false }))
    }
  },
})

/**
 * Listener: Refresh Asset Data
 * Fetches the ordered live entries + source-file roster. Entries/references go into the
 * shared billing-summary slices keyed by assetId (the table/popovers read by parent id).
 */
startListener({
  actionCreator: MedSummaryAssetActions.refreshAssetData,
  effect: async (action, listenerApi) => {
    const { assetId } = action.payload

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setDataError({ assetId, hasError: false }))

      const [entriesResponse, filesResponse] = await Promise.all([getMedSummaryAssetEntries(assetId), getMedSummaryAssetFiles(assetId)])

      listenerApi.dispatch(MedSummaryEntriesActions.setEntriesList({ parentId: assetId, entries: entriesResponse.entries }))
      listenerApi.dispatch(MedSummaryReferencesActions.setReferencesRecord({ parentId: assetId, references: entriesResponse.references }))
      listenerApi.dispatch(MedSummaryAssetActions.setLoadedEntriesVersion({ assetId, version: entriesResponse.summary_version }))
      listenerApi.dispatch(MedSummaryAssetActions.setFilesForAsset({ assetId, files: filesResponse.files }))
    } catch {
      listenerApi.dispatch(MedSummaryAssetActions.setDataError({ assetId, hasError: true }))
    }
  },
})

/**
 * Listener: Fetch Update History
 * Loads the asset's full update history (newest-first) into the store for the version panel.
 */
startListener({
  actionCreator: MedSummaryAssetActions.fetchUpdateHistory,
  effect: async (action, listenerApi) => {
    const { assetId } = action.payload

    try {
      const response = await getMedSummaryAssetUpdates(assetId)
      listenerApi.dispatch(MedSummaryAssetActions.setUpdateHistory({ assetId, updates: response.updates }))
    } catch (e) {
      console.error('Error fetching billing summary update history:', e)
    }
  },
})

/**
 * Listener: Poll Active Update
 * Polls one update run with 5s -> 10s -> 15s backoff until it leaves the active statuses.
 * De-dupes per asset so re-dispatching for an asset already polling is a no-op, while
 * different assets still poll concurrently (so switching matters doesn't drop a poll).
 * Exits: DONE -> refresh overview + data; FAILED_FILES -> keep status + refresh roster;
 * ERROR -> keep status (update.id doubles as the user-facing error ID).
 */
startListener({
  actionCreator: MedSummaryAssetActions.pollActiveUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    if (pollingAssetIds.has(assetId)) return
    pollingAssetIds.add(assetId)
    try {
      let pollCount = 0
      let consecutiveErrors = 0

      for (;;) {
        // A transient status-fetch error must not kill the loop; swallow it and retry on
        // the next backoff tick so the banner keeps polling instead of spinning forever.
        let status
        try {
          status = await getUpdateStatus(assetId, updateId)
          consecutiveErrors = 0
        } catch (e) {
          consecutiveErrors++
          console.error(`Error polling billing summary update (failure ${consecutiveErrors}/${kMedSummaryAssetMaxPollFailures}):`, e)
          if (consecutiveErrors >= kMedSummaryAssetMaxPollFailures) return
          const delay = kMedSummaryAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kMedSummaryAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        listenerApi.dispatch(MedSummaryAssetActions.setUpdateStatus({ assetId, status }))

        if (kActiveMedSummaryUpdateStatuses.includes(status.update.status)) {
          const delay = kMedSummaryAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kMedSummaryAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        if (status.update.status === MedSummaryUpdateStatus.DONE) {
          const overview = await getMedSummaryAsset(assetId)
          listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
          listenerApi.dispatch(MedSummaryAssetActions.refreshAssetData({ assetId }))
          listenerApi.dispatch(MedSummaryAssetActions.fetchUpdateHistory({ assetId }))
        } else if (status.update.status === MedSummaryUpdateStatus.FAILED_FILES) {
          const files = await getMedSummaryAssetFiles(assetId)
          listenerApi.dispatch(MedSummaryAssetActions.setFilesForAsset({ assetId, files: files.files }))
        }
        return
      }
    } catch (e) {
      console.error('Error polling billing summary update:', e)
    } finally {
      // Release the per-asset guard so a later run on this asset (or coming back to the matter)
      // can start a fresh poll loop. Without this the asset stays "polling" forever and re-dispatches no-op.
      pollingAssetIds.delete(assetId)
    }
  },
})

/**
 * Listener: Initialize Summary
 * Creates the living billing summary from the staged file selection and starts polling its
 * INITIALIZE run. A 409 means the matter already has one - refetch and surface inline.
 */
startListener({
  actionCreator: MedSummaryAssetActions.initializeSummary,
  effect: async (action, listenerApi) => {
    const { matterId, userFileIds } = action.payload

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setInitializePending({ matterId, isPending: true }))
      listenerApi.dispatch(MedSummaryAssetActions.setInitializeError({ matterId, error: null }))

      const response = await postInitializeSummary({ matter_id: matterId, user_file_ids: userFileIds })
      listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId, asset: response.asset }))
      listenerApi.dispatch(MedSummaryAssetActions.pollActiveUpdate({ assetId: response.asset.id, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(MedSummaryAssetActions.resetAddFilesStaging({ matterId }))
    } catch (e) {
      const message = e instanceof ApiError && e.status === 409 ? 'This matter already has a billing summary.' : kGenericMutationMessage
      listenerApi.dispatch(MedSummaryAssetActions.setInitializeError({ matterId, error: message }))
      if (e instanceof ApiError && e.status === 409) {
        listenerApi.dispatch(MedSummaryAssetActions.fetchAssetForMatter({ matterId }))
      }
    } finally {
      listenerApi.dispatch(MedSummaryAssetActions.setInitializePending({ matterId, isPending: false }))
    }
  },
})

/**
 * Listener: Apply Update (Add Files)
 * Enqueues an ADD_FILES run and starts polling it. A 409 means another run is active -
 * surface inline and refetch the overview so the UI shows that run.
 */
startListener({
  actionCreator: MedSummaryAssetActions.applyUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, addUserFileIds } = action.payload

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: null }))

      const response = await postApplyUpdate(assetId, { add_user_file_ids: addUserFileIds })

      const overview = await getMedSummaryAsset(assetId)
      listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      // The roster rows for the added files are created synchronously when the run is enqueued, so
      // refresh the Source Files roster now — they show as Pending/Processing instead of only after DONE.
      const files = await getMedSummaryAssetFiles(assetId)
      listenerApi.dispatch(MedSummaryAssetActions.setFilesForAsset({ assetId, files: files.files }))
      listenerApi.dispatch(MedSummaryAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(MedSummaryAssetActions.resetAddFilesStaging({ matterId: overview.matter_id }))
    } catch (e) {
      const message = e instanceof ApiError && e.status === 409 ? kConflictMessage : kGenericMutationMessage
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: message }))
      if (e instanceof ApiError && e.status === 409) {
        try {
          const overview = await getMedSummaryAsset(assetId)
          listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
          if (overview.active_update) {
            listenerApi.dispatch(MedSummaryAssetActions.pollActiveUpdate({ assetId, updateId: overview.active_update.id }))
          }
        } catch (refetchError) {
          console.error('Error refetching billing summary after conflict:', refetchError)
        }
      }
    } finally {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Retry Failed Files
 * Re-enqueues a FAILED_FILES run for its failed files only, then resumes polling.
 */
startListener({
  actionCreator: MedSummaryAssetActions.retryFailedFiles,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: null }))

      const response = await postRetryFailed(assetId, updateId)
      // Refresh the overview so active_update points to the retry run. Otherwise the stale
      // FAILED_FILES active_update keeps selectFailedFilesUpdate returning the old decision, so the
      // "Retry / Continue without failed files" banner stays up while the retry is already running.
      const overview = await getMedSummaryAsset(assetId)
      listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(MedSummaryAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
    } catch {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Complete Excluding Failed
 * Publishes the staged successes of a FAILED_FILES run and excludes its failures,
 * then refreshes the overview and billing summary data.
 */
startListener({
  actionCreator: MedSummaryAssetActions.completeExcludingFailed,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: null }))

      const status = await postCompleteExcludingFailed(assetId, updateId)
      listenerApi.dispatch(MedSummaryAssetActions.setUpdateStatus({ assetId, status }))

      const overview = await getMedSummaryAsset(assetId)
      listenerApi.dispatch(MedSummaryAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(MedSummaryAssetActions.refreshAssetData({ assetId }))
    } catch {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(MedSummaryAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Stage Initialized Upload
 * Converts a pending Add Files upload into a staged file the moment its user_file id exists.
 * Keyed on the shared uppy action because BOTH upload drivers dispatch it — the Add Files
 * page pipeline and the MatterLayout fallback that adopts uploads stranded when the page
 * unmounts mid-batch. Living here (not in the page hook) is what guarantees a fallback-driven
 * upload still lands in the staged selection instead of deadlocking the CTA as a forever-pending
 * card. Uploads not registered by the billing-summary Add Files page are never in the attached list.
 */
startListener({
  actionCreator: UppyFileUploadActions.fileInitialized,
  effect: (action, listenerApi) => {
    const { clientTmpId, serverFileId } = action.payload
    const attachedPendingIds = listenerApi.getState().medSummaryAssetState.addFilesStaging.attachedPendingUploadClientIds
    if (!attachedPendingIds.includes(clientTmpId)) return

    listenerApi.dispatch(MedSummaryAssetActions.stageUserFileIds({ fileIds: [serverFileId] }))
    listenerApi.dispatch(MedSummaryAssetActions.removePendingUserFileUpload(clientTmpId))
  },
})
